import styled, { css } from "styled-components/macro";
import SummaryBoxPrevCurrShift from "./SummaryBoxPreviousShift/SummaryBoxPrevCurrShift";
import MeritTier0HourlyTableChart from "./MeritTier0HourlyTableChart/MeritTier0HourlyTableChart";
import { defaultSupersetTheme } from "./utils/defaultSupersetTheme";
import { useSelector } from "store/useSelector";
import {
  DataDumpAggregateType,
  DataDumpRow,
  TimeRangeFinite,
} from "types/types";
import {
  ChartPluginsProps,
  DataDumpAggregate,
  TimeseriesDataRecord,
} from "./MeritTier0HourlyTableChart/types";
import Spinner from "components/SimpleComponents/Spinner/Spinner";
import RangeSelector from "components/Dashboard/range-selectors/RangeSelector";
import { useDataDumpRows } from "components/Dashboard/HubletView/SingleTimeline/singleTimelineStore";
import {
  useFocusRange,
  useFormatZoned,
  useSelectedShift,
  useTrendChartDataDumps,
  useVisibleProcesses,
} from "utils/customHooks/genericHooks";
import { ONE_HOUR_OF_MS, ONE_MINUTE_OF_MS } from "utils/timeUtils";
import { atom, useAtom } from "jotai";
import DataDumpGetter from "segments/DataDumpGetter/DataDumpGetter";
import ErrorBoundary from "components/SimpleComponents/Errors/ErrorBoundary";
import { useLocation } from "react-router";
import { InitTier0 } from "./InitTier0";
import { useResetTrendChartBinSize } from "pages/DashboardPages/dashboardPagesUtils";
import { TrendChartBinSizesEnum } from "types/enums";
import { prevShiftDDAAtom, useProcessIdsMapOnTier0Page } from "./tier0Utils";
import useQueryWithErrorHandling from "utils/useQuery";
import {
  DATA_DUMP_ROWS_QUERY,
  midgardClients,
} from "providers/MidgardProvider";
import { useCustomerKey } from "utils/customHooks/useCustomerKey";
import CardWithControls from "components/Dashboard/CardWithControls/CardWithControls";
import { BREAKPOINT_DESKTOP, useBreakpoints } from "constants/constants";
import RangeSelectorConfig from "components/Dashboard/range-selectors/RangeSelectorConfig";
import { Button, Collapse, IconButton, Tooltip } from "@mui/material";
import { ArrowForwardIos } from "@mui/icons-material";
import { useEffect, useState } from "react";
import { blueButton, cardBorder } from "constants/colors";
import { useMount } from "react-use";
import StayOnCurrentShift from "components/Dashboard/range-selectors/StayOnCurrentShift";
import { useFlightConfig } from "components/DataGetter/FlightConfig/useFlightConfig";

export default function Tier0Page() {
  // set trend chart to hourly bin size
  useResetTrendChartBinSize({
    newTrendChartBinSize: TrendChartBinSizesEnum.hours,
  });
  const { isDesktopOrLarger, isDesktopXlOrLarger } = useBreakpoints();
  const location = useLocation();
  const [rangeSelectorHidden, setRangeSelectorHidden] = useState(
    location.search.includes("rangeSelectorHidden=true")
  );

  const hideNav = location.search.includes("hideNav=true");

  return (
    <MeritTier0FlightStyles>
      <ErrorBoundary>
        <StayOnCurrentShift />
      </ErrorBoundary>
      <ErrorBoundary>
        <InitTier0 />
      </ErrorBoundary>
      <ErrorBoundary>
        <AdditionalDataGetters />
      </ErrorBoundary>
      {hideNav ? null : (
        <>
          <Tooltip
            title={`${rangeSelectorHidden ? "Show" : "Hide"} Range Selector`}
          >
            <IconButton
              onClick={() => setRangeSelectorHidden(!rangeSelectorHidden)}
              style={{
                zIndex: 1,
                position: "absolute",
                top: (isDesktopXlOrLarger ? 85 : 54) + (hideNav ? -64 : 0),
                left: "calc(-23px + 50vw)",
                transition: "transform 0.3s",
                transform: `rotate(${rangeSelectorHidden ? 90 : 270}deg)`,
              }}
            >
              <ArrowForwardIos />
            </IconButton>
          </Tooltip>

          <Collapse in={!rangeSelectorHidden} style={{ marginBottom: -10 }}>
            <ErrorBoundary>
              <CardWithControls
                title=""
                customCss={css`
                  text-align: left;
                  padding: 16px;
                  overflow: visible;
                  @media (min-width: ${BREAKPOINT_DESKTOP}px) {
                    padding: 16px 64px;
                  }
                  &&& {
                    margin-top: 10px;
                    margin-bottom: 0;
                  }
                  .cardTopbar {
                    background: none;
                    pointer-events: none;
                    .cardConfigControls {
                      pointer-events: all;
                      transform: translate(64px, -16px);
                    }
                  }
                  [class*="DateAndShiftPickerStyles"] {
                    max-width: unset;
                  }
                `}
                shouldOverlayTitle={true}
                showCardBorder={!isDesktopOrLarger}
                CardConfigControls={<RangeSelectorConfig />}
              >
                <RangeSelector />
              </CardWithControls>
            </ErrorBoundary>
          </Collapse>
        </>
      )}

      <ErrorBoundary>
        <CardWithControls
          title=""
          customCss={css`
            padding: 16px;
            .cardTopbar {
              display: none;
            }
          `}
          shouldOverlayTitle={true}
          showCardBorder={!isDesktopOrLarger}
        >
          <SummaryBoxConnected />
        </CardWithControls>
      </ErrorBoundary>
      <ErrorBoundary>
        <MeritTier0HourlyTableChartConnected />
      </ErrorBoundary>
    </MeritTier0FlightStyles>
  );
}

const MeritTier0FlightStyles = styled.div`
  &&& {
    .total-downtime {
      /* height: 28px;
      transform: translateY(-12px); */
      padding: 0 0 0 24px;
      vertical-align: middle;
    }
    tr.last {
    }
    td.target,
    td.output,
    .totals-row td {
      vertical-align: middle;
    }
    [class*="MeritTier0HourlyTableChart__Styles"] {
      overflow-y: auto;
      min-height: 0;
      padding: 0 10px;
      max-width: unset;
      [class*="MeritTier0HourlyTableChart__CardStyles"] {
        margin-top: 0 !important;
      }
    }
    .head-count-container {
      padding-top: 1px;
      padding-right: 2px;
    }
    .work-order-container {
      padding-top: 1px;
      padding-right: 1px;
    }

    .totals-row {
      .title {
        width: 9.4% !important;
      }
      .work-orders {
        width: 27.6% !important;
      }
      .output {
        width: 6.9% !important;
      }
      .target {
        width: 7.8% !important;
      }
      .hourly-performance {
        width: 7.8% !important;
      }
      .total-downtime {
        min-width: 0 !important;
        width: 21.5% !important;
      }
    }
  }
`;

function AdditionalDataGetters() {
  return (
    <>
      <PrevShiftDatadumpGetter />
      <PrevShiftDatadumpRowsGetter />
    </>
  );
}

function PrevShiftDatadumpGetter() {
  const focusRange = useFocusRange();
  const { search } = useLocation();
  const urlSearchParams = new URLSearchParams(search);
  const urlProcessName = urlSearchParams.get("process");
  const [, setPrevShiftDDA] = useAtom(prevShiftDDAAtom);
  const focusRangeDuration = focusRange ? focusRange.lt - focusRange.ge : null;
  const prevShiftFocusRange =
    focusRange && focusRangeDuration
      ? {
          ge: focusRange.ge - focusRangeDuration,
          lt: focusRange.ge,
        }
      : null;
  const processIdsMap = useProcessIdsMapOnTier0Page();
  const processId =
    urlProcessName &&
    Object.entries(processIdsMap).reduce((acc, [_groupName, processesObj]) => {
      Object.entries(processesObj).forEach(([processId, processName]) => {
        if (processName === urlProcessName) {
          acc = processId;
        }
      });
      return acc;
    }, "");

  return !processId || !prevShiftFocusRange ? null : (
    <DataDumpGetter
      {...{
        dataDumpAggType: "UPDATE_CUSTOM_DATA_DUMP_FOR_PROCESSES",
        processIds: [processId],
        timeRange: prevShiftFocusRange,
        customOnCompleted: (data: { dataDumpAggregate: DataDumpAggregate }) => {
          if (data?.dataDumpAggregate) {
            setPrevShiftDDA(data.dataDumpAggregate);
          }
        },
      }}
    />
  );
}
const prevShiftDDRowsAtom = atom<DataDumpRow[] | null>(null);
function PrevShiftDatadumpRowsGetter() {
  const focusRange = useFocusRange();
  const { search } = useLocation();
  const urlSearchParams = new URLSearchParams(search);
  const urlProcessName = urlSearchParams.get("process");
  const [, setPrevShiftDDRows] = useAtom(prevShiftDDRowsAtom);
  const focusRangeDuration = focusRange ? focusRange.lt - focusRange.ge : null;
  const prevShiftFocusRange =
    focusRange && focusRangeDuration
      ? {
          ge: focusRange.ge - focusRangeDuration,
          lt: focusRange.ge,
        }
      : null;
  const processIdsMap = useProcessIdsMapOnTier0Page();
  const processId =
    urlProcessName &&
    Object.entries(processIdsMap).reduce((acc, [_groupName, processesObj]) => {
      Object.entries(processesObj).forEach(([processId, processName]) => {
        if (processName === urlProcessName) {
          acc = processId;
        }
      });
      return acc;
    }, "");

  const ckey = useCustomerKey();
  const client = midgardClients.get(ckey);
  useQueryWithErrorHandling("DATA_DUMP_ROWS_TIER0", DATA_DUMP_ROWS_QUERY, {
    client,
    variables: {
      ge: String(prevShiftFocusRange?.ge),
      lt: String(prevShiftFocusRange?.lt),
      ckey,
      machemetryIds: [processId],
    },
    skip: !prevShiftFocusRange,
    onCompleted: data => {
      if (!data.dataDump) {
        console.log("🚨 no dataDump found", data);
      }
      setPrevShiftDDRows(data.dataDump);
    },
  });

  return null;
}

function SummaryBoxConnected() {
  const dataDumpAggregate = useSelector(
    state => state.legacy.dataDumpFullRange
  );
  const formatZoned = useFormatZoned();
  const [prevShiftDDA] = useAtom(prevShiftDDAAtom);
  const [prevShiftDDRows] = useAtom(prevShiftDDRowsAtom);
  const uniqueSkusPrevShift = prevShiftDDRows?.reduce(
    (acc, row) => (acc.includes(row.skuCode) ? acc : [...acc, row.skuCode]),
    [] as string[]
  );
  const prevShiftNumCompletedOrders =
    Math.max(0, (uniqueSkusPrevShift?.length ?? 0) - 1) ?? 0;

  const dateReadable = dataDumpAggregate
    ? formatZoned(dataDumpAggregate.range.ge, "EEEE, MMMM d, yyyy")
    : "--";
  const { search } = useLocation();
  const urlProcessName = new URLSearchParams(search).get("process");
  const lineName = urlProcessName ?? "--";
  const selectedShift = useSelectedShift();
  const shiftName = selectedShift?.shift.name ?? "--";

  const prevShiftHeadcount = prevShiftDDA?.lastSegment?.teamSize ?? 0;

  const prevShiftTotalUnitsProduced = prevShiftDDA?.netOutput ?? 0;
  return (
    <SummaryBoxPrevCurrShift
      key="summary-box-prev-curr-shift"
      {...{
        dateReadable,
        leadPersonName: "<NAME>",
        lineName,
        prevShiftHeadcount,
        prevShiftNumCompletedOrders,
        prevShiftTotalUnitsProduced,
        prevShiftLoading: !prevShiftDDA,
        shiftName,
        supervisorName: "<NAME>",
        css: css``,
      }}
    />
  );
}
function MeritTier0HourlyTableChartConnected() {
  const dataDumpFullRange = useSelector(
    state => state.legacy.dataDumpFullRange
  );
  const [allDataDumpRows] = useDataDumpRows();
  const visibleProcesses = useVisibleProcesses();
  const { search } = useLocation();
  const urlProcessName = new URLSearchParams(search).get("process");
  const selectedProcessId = urlProcessName
    ? visibleProcesses.find(p => p.name === urlProcessName)?.id
    : null;

  const dataDumpRowsForProcess =
    selectedProcessId && allDataDumpRows?.[selectedProcessId]
      ? allDataDumpRows[selectedProcessId]
      : [];
  const timeSeriesDataForProcess = dataDumpRowsForProcess
    ? tier0TimeSeriesFromFlightDDRows(dataDumpRowsForProcess)
    : [];

  const trendChartDDAs = useTrendChartDataDumps();
  const hourlyDataDumpAggregates = tier0AggregatesFromDDAs(trendChartDDAs);
  const [
    {
      general: { hideProducingLosses, includeDependentLosses },
    },
  ] = useFlightConfig();
  const hourlyTopDowntimes = tier0HourlyTopDowntimesFromDDAs(
    trendChartDDAs,
    dataDumpRowsForProcess,
    hideProducingLosses || !includeDependentLosses
  );

  const selectedShift = useSelectedShift();
  const totalDowntime =
    (dataDumpFullRange?.unknownTime ?? 0) +
    (dataDumpFullRange?.waitingTime ?? 0) +
    (dataDumpFullRange?.unplannedTime ?? 0) +
    (dataDumpFullRange?.plannedTime ?? 0) +
    (dataDumpFullRange?.qualityLossTime ?? 0) +
    (dataDumpFullRange?.minorStopTime ?? 0) +
    (dataDumpFullRange?.rejectLossTime ?? 0) +
    (dataDumpFullRange?.notScheduledTime ?? 0);
  const focusRange = useFocusRange();
  const totalTime = focusRange ? focusRange.lt - focusRange.ge : 0;
  const totalDowntimePercent = (totalDowntime / totalTime) * 100;

  const dataUnfiltered = dataDumpRowsForProcess.sort(
    (a, b) => Number(a.ge) - Number(b.ge)
  );
  const { maxLt } = dataUnfiltered.reduce(
    (acc, { ge, lt }) => ({
      minGe: Math.min(acc.minGe, Number(ge) === 0 ? Infinity : Number(ge)),
      maxLt: Math.max(acc.maxLt, Number(lt)),
    }),
    {
      minGe: Infinity,
      maxLt: 0,
    }
  );
  const teamSizeChangesHistory: {
    teamSize: number;
    ge: number;
    lt: number;
  }[] = tier0TeamSizesHistoryFromDDRows(dataDumpRowsForProcess, maxLt);
  const skuCodeChangesHistory: {
    skuCode: string | null;
    skuName: string | null;
    ge: number;
    lt: number;
    isAboveTarget: boolean;
    target: number;
    output: number;
  }[] = tier0TeamSkuCodeChangesHistoryFromDDRows(
    dataDumpRowsForProcess,
    focusRange
  );

  const now = Date.now();
  const isCurrentShift = Boolean(
    focusRange && focusRange.ge <= now && now < focusRange.lt
  );

  const loading = !dataDumpFullRange;
  // if we're ever stuck loading for >10s, show a "server unresponsive, try again" indicator
  const [stuckLoading, setStuckLoading] = useState(false);
  useEffect(() => {
    let timer;
    if (loading) {
      timer = window.setTimeout(() => {
        setStuckLoading(true);
      }, 10 * 1000);
    } else if (!loading && stuckLoading) {
      setStuckLoading(false);
    }
    return () => {
      window.clearTimeout(timer);
    };
  }, [loading, stuckLoading]);

  return loading ? (
    <div style={{ padding: 96 }}>
      {stuckLoading ? (
        <StuckLoadingIndicator {...{ setStuckLoading }} />
      ) : (
        <Spinner />
      )}
    </div>
  ) : (
    <MeritTier0HourlyTableChart
      key="hourly-table-chart"
      {...{
        data: timeSeriesDataForProcess,
        dataDumpAggregate: tier0DDAFromFlightDDA(dataDumpFullRange),
        dataDumpRows: dataDumpRowsForProcess,
        hourlyDataDumpAggregates,
        hourlyTopDowntimes,
        skuCodeChangesHistory,
        teamSizeChangesHistory,
        shiftName: selectedShift.shift.name ?? "",
        totalDowntime:
          // round to nearest minute
          Math.round(totalDowntime / ONE_MINUTE_OF_MS) * ONE_MINUTE_OF_MS,
        totalDowntimePercent,
        theme: defaultSupersetTheme,
        boldText: true,
        headerFontSize: "xl",
        headerText: "Header Text",
        height: "1200px",
        width: "100%",
        isCurrentShift,
        isPartialDataset: false,
        css: css``,
      }}
    />
  );
}

function StuckLoadingIndicator({ setStuckLoading }) {
  // set document title in case autohotkey script needs to detect it
  useMount(() => {
    if (!document.title.includes("Server not responding")) {
      document.title = document.title + " - Server not responding";
    }
    return () => {
      document.title = document.title.replace(" - Server not responding", "");
    };
  });
  return (
    <StuckLoadingIndicatorStyles>
      <h1>Server not responding</h1>
      <p>Unable to access shift data right now.</p>
      <p>Please try again later.</p>
      <div className="actionsRow">
        <Button
          size="small"
          variant="contained"
          onClick={() => {
            window.location.reload();
          }}
        >
          Refresh
        </Button>
        <Button
          size="small"
          variant="outlined"
          onClick={() => {
            setStuckLoading(false);
          }}
        >
          Dismiss
        </Button>
      </div>
    </StuckLoadingIndicatorStyles>
  );
}
const StuckLoadingIndicatorStyles = styled.div`
  min-width: 336px;
  h1 {
    font-size: 1.5em;
    margin-bottom: 0.75em;
  }
  p {
    font-size: 1em;
    line-height: 1.5em;
  }
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 2em 1.5em 1.5em 2em;
  border: 1px solid ${cardBorder};
  background: white;
  width: fit-content;
  margin: auto;
  border-radius: 8px;
  .actionsRow {
    margin-top: 1.5em;
    justify-content: right;
    display: flex;
    gap: 0.5em;
    button {
      padding: 4px 18px;
    }
    button:not(.MuiButton-outlined) {
      background: ${blueButton};
      color: white;
    }
  }
`;

function tier0TeamSkuCodeChangesHistoryFromDDRows(
  dataDumpRowsForProcess: DataDumpRow[],
  focusRange: TimeRangeFinite | null
): {
  skuCode: string | null;
  skuName: string | null;
  ge: number;
  lt: number;
  isAboveTarget: boolean;
  target: number;
  output: number;
}[] {
  const skuCodeChangesHistory = dataDumpRowsForProcess.reduce(
    (acc, row, idx, all) => {
      const prevSkuCodeChangeRecord = acc[acc.length - 1];
      const isSameSkuAsPrev =
        prevSkuCodeChangeRecord &&
        row.skuCode === prevSkuCodeChangeRecord.skuCode;
      const didSkuChange =
        prevSkuCodeChangeRecord &&
        row.skuCode !== undefined &&
        row.skuCode !== null &&
        row.skuCode !== prevSkuCodeChangeRecord.skuCode;
      const { ge, lt } = {
        ge: Number(row.ge),
        lt: Number(row.lt),
      };
      const duration = lt - ge;

      if (!row.skuCode || !row.skuName) {
        // if there's no skuCode or skuName, don't add to history...
        return acc;
      }
      // if skuCode is same as previous, add output + target to the previous record
      else if (isSameSkuAsPrev) {
        prevSkuCodeChangeRecord.output += row.netOutput;
        const target = getRowTarget(row, duration / ONE_HOUR_OF_MS);
        prevSkuCodeChangeRecord.target += target;
        prevSkuCodeChangeRecord.lt = lt;
        // if skuCode is different from the previous, add a skuCodeChange to the acc
      } else if (didSkuChange || acc.length === 0) {
        const prevRow = all[idx - 1];
        // end the previous record at the current's start
        if (prevSkuCodeChangeRecord) {
          prevSkuCodeChangeRecord.lt = Number(prevRow?.lt ?? row.ge);
        }
        const target = getRowTarget(row, duration / ONE_HOUR_OF_MS);
        const skuCodeChangeRecord = {
          ge,
          lt,
          skuCode: row.skuCode,
          skuName: row.skuName,
          isAboveTarget: row.netOutput >= target,
          target,
          output: row.netOutput,
        };

        acc = [...acc, skuCodeChangeRecord];
      }
      return acc;
    },
    [] as {
      skuCode: string | null;
      skuName: string | null;
      ge: number;
      lt: number;
      isAboveTarget: boolean;
      target: number;
      output: number;
    }[]
  );
  // ensure the last item has lt = range.lt (or current time)
  const lastSkuCodeChangeRecord =
    skuCodeChangesHistory[skuCodeChangesHistory.length - 1];
  if (focusRange && lastSkuCodeChangeRecord) {
    lastSkuCodeChangeRecord.lt = Math.min(Date.now(), focusRange.lt);
  }
  // calculate isAboveTarget
  const skuCodeChangesHistoryWithIsAboveTarget = skuCodeChangesHistory.map(
    skuCodeChangeRecord => {
      return {
        ...skuCodeChangeRecord,
        isAboveTarget: skuCodeChangeRecord.output >= skuCodeChangeRecord.target,
      };
    }
  );
  return skuCodeChangesHistoryWithIsAboveTarget;
}

function getRowTarget(row: DataDumpRow, duration: number) {
  return ["planned", "notScheduled"].includes(row.pnlCategory)
    ? 0
    : row.targetRate * duration; // (midgard targetRate already accounts for teamSize & skuScale)
}

function tier0TeamSizesHistoryFromDDRows(
  dataDumpRowsForProcess: DataDumpRow[],
  maxLt: number
): { teamSize: number; ge: number; lt: number }[] {
  const teamSizeHistory = dataDumpRowsForProcess
    .filter(row => row.pnlCategory !== "uncollected")
    .map(row => ({
      ge: Number(row.ge),
      lt: Number(row.lt),
      teamSize: row.teamSize,
    }));

  const teamSizeChangesHistory = teamSizeHistory.reduce(
    (acc, cur, idx) => {
      // first row
      if (acc.length === 0 && cur.teamSize) {
        acc.push(cur);
        return acc;
      }
      // subsequent row - if team size changes, add to history
      const prevTeamSize = acc[acc.length - 1].teamSize;
      if (cur.teamSize && prevTeamSize && prevTeamSize !== cur.teamSize) {
        acc.push(cur);
        // if team size didn't change, adjust the previous entry's lt
      } else if (prevTeamSize === cur.teamSize) {
        acc[acc.length - 1].lt = Number(cur.lt);
        // if it's the last row, use the last lt
      } else if (idx === teamSizeHistory.length - 1) {
        acc[acc.length - 1].lt = maxLt;
      }
      return acc;
    },
    [] as { teamSize: number; ge: number; lt: number }[]
  );

  return teamSizeChangesHistory;
}

function tier0DDAFromFlightDDA(
  dataDumpAggregate: DataDumpAggregateType
): DataDumpAggregate {
  return {
    ge: String(dataDumpAggregate.range.ge),
    lt: String(dataDumpAggregate.range.lt),
    producingTime: dataDumpAggregate.producingTime ?? 0,
    productionTime: dataDumpAggregate.productionTime ?? 0,
    plannedTime: dataDumpAggregate.plannedTime ?? 0,
    waitingTime: dataDumpAggregate.waitingTime ?? 0,
    unplannedTime: dataDumpAggregate.unplannedTime ?? 0,
    unknownTime: dataDumpAggregate.unknownTime ?? 0,
    notScheduledTime: dataDumpAggregate.notScheduledTime ?? 0,
    qualityLossTime: dataDumpAggregate.qualityLossTime ?? 0,
    uncollectedTime: dataDumpAggregate.uncollectedTime ?? 0,
    grossOutput: dataDumpAggregate.grossOutput ?? 0,
    rejectOutput: dataDumpAggregate.rejectOutput ?? 0,
    outputLossSum: dataDumpAggregate.outputLossSum ?? 0,
    productionPotentialOutput: dataDumpAggregate.productionPotentialOutput ?? 0,
    totalPotentialOutput: dataDumpAggregate.totalPotentialOutput ?? 0,
    netOutput: dataDumpAggregate.netOutput ?? 0,
    pnlLosses: {
      ...dataDumpAggregate.pnlLosses,
      asOf: "",
      range: {
        ge: String(dataDumpAggregate.range.ge),
        lt: String(dataDumpAggregate.range.lt),
      },
    } as unknown as DataDumpAggregate["pnlLosses"],
  };
}

function tier0TimeSeriesFromFlightDDRows(
  dataDumpRows: DataDumpRow[]
): TimeseriesDataRecord[] {
  return dataDumpRows.map(row => {
    return {
      ...row,
      __timestamp: 0,
    };
  });
}

function tier0AggregatesFromDDAs(
  dataDumpAggregates: DataDumpAggregateType[]
): DataDumpAggregate[] {
  return dataDumpAggregates.map(aggregate => {
    return tier0DDAFromFlightDDA(aggregate);
  });
}

function tier0HourlyTopDowntimesFromDDAs(
  dataDumpAggregates: DataDumpAggregateType[],
  dataDumpRowsForProcess: DataDumpRow[],
  skipProducingLosses?: boolean
): ChartPluginsProps["hourlyTopDowntimes"] {
  return dataDumpAggregates.map((agg, idx) => {
    const sortedRows = [...dataDumpRowsForProcess].sort((a, b) => {
      const rowADuration = Number(a.lt) - Number(a.ge);
      const rowBDuration = Number(b.lt) - Number(b.ge);
      return rowBDuration - rowADuration;
    });

    const biggestDowntimeTag = sortedRows.reduce(
      (acc, row) => {
        const isRowWithinHour =
          !row.lt ||
          (agg.range.ge <= Number(row.ge) && Number(row.ge) < agg.range.lt) ||
          (agg.range.ge <= Number(row.lt) && Number(row.lt) < agg.range.lt) ||
          (Number(row.ge) <= agg.range.ge && agg.range.lt < Number(row.lt));

        const rowDuration = Number(row.lt) - Number(row.ge);
        const isDowntimeTag =
          row.tagName &&
          row.tagName !== "No Production Scheduled" &&
          [
            "unplanned",
            "unknown",
            "waiting",
            "planned",
            "notScheduled",
          ].includes(row.pnlCategory);

        if (isRowWithinHour && isDowntimeTag) {
          const isBiggestDuration = !acc.agg_reason;
          const isSecondBiggestBiggestDuration =
            row.tagName && !acc.done && row.tagName !== acc.agg_reason;
          if (isBiggestDuration && row.tagName) {
            return {
              agg_reason: row.tagName,
              duration: rowDuration,
              done: false,
              agg,
            };
          } else if (isSecondBiggestBiggestDuration && row.tagName) {
            return {
              ...acc,
              agg_reason: acc.agg_reason + " / " + (row.tagName ?? ""),
              done: true,
            };
          }
          return acc;
        }
        return acc;
      },
      { agg_reason: "", duration: 0, done: false, agg } as {
        agg_reason: string;
        duration: number;
        done: boolean;
        agg: DataDumpAggregateType;
      }
    );

    return {
      agg_order: idx,
      agg_reason: biggestDowntimeTag.agg_reason,
      agg_total_downtime_min: Math.round(
        ((agg.waitingTime ?? 0) +
          (agg.unknownTime ?? 0) +
          (agg.unplannedTime ?? 0) +
          (agg.plannedTime ?? 0) +
          ((skipProducingLosses ? 0 : agg.minorStopTime) ?? 0) +
          ((skipProducingLosses ? 0 : agg.rejectLossTime) ?? 0) +
          (agg.notScheduledTime ?? 0) +
          ((skipProducingLosses ? 0 : agg.qualityLossTime) ?? 0)) /
          ONE_MINUTE_OF_MS
      ),
      agg,
    };
  });
}
